import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { User, Target, ArrowRight } from 'lucide-react'
import { config } from '../config'

export default function Onboarding() {
  const navigate = useNavigate()
  const [formData, setFormData] = useState({
    role: '',
    team: '',
    product_value: '',
    industry: '',
    business_focus: '',
    customer_segments: [] as string[],
    customer_locations: [] as string[]
  })
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [isLoading, setIsLoading] = useState(false)
  const [isSuccess, setIsSuccess] = useState(false)

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
    
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }))
    }
  }

  const handleCheckboxChange = (name: string, value: string, checked: boolean) => {
    setFormData(prev => ({
      ...prev,
      [name]: checked 
        ? [...prev[name as keyof typeof prev] as string[], value]
        : (prev[name as keyof typeof prev] as string[]).filter(item => item !== value)
    }))
  }

  const validateForm = () => {
    const newErrors: Record<string, string> = {}

    if (!formData.role) newErrors.role = 'Role is required'
    if (!formData.team) newErrors.team = 'Team is required'
    if (!formData.product_value) newErrors.product_value = 'Product value proposition is required'
    if (!formData.industry) newErrors.industry = 'Industry is required'
    if (!formData.business_focus) newErrors.business_focus = 'Business focus is required'
    if (formData.customer_segments.length === 0) newErrors.customer_segments = 'Please select at least one customer segment'
    if (formData.customer_locations.length === 0) newErrors.customer_locations = 'Please select at least one customer location'

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!validateForm()) return

    setIsLoading(true)
    setErrors({})

    try {
      const resp = await fetch(`${config.apiBaseUrl}/api/users/onboarding`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(formData),
      })
      if (!resp.ok) {
        throw new Error('Onboarding failed')
      }
      setIsSuccess(true)
      // After success, redirect to settings
      setTimeout(() => {
        navigate('/setup')
      }, 1200)
    } catch (err) {
      setErrors({ general: 'Failed to save your profile. Please try again.' })
    } finally {
      setIsLoading(false)
    }
  }

  const handleSkip = async () => {
    // Mark onboarding completed with minimal payload
    setIsLoading(true)
    try {
      const resp = await fetch(`${config.apiBaseUrl}/api/users/onboarding`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({}),
      })
      if (resp.ok) {
        navigate('/setup')
      } else {
        navigate('/setup')
      }
    } finally {
      setIsLoading(false)
    }
  }

  if (isSuccess) {
    return (
      <div className="min-h-screen flex flex-col justify-center py-12 sm:px-6 lg:px-8" style={{ background: "linear-gradient(135deg, #36d1dc 0%, #5b86e5 100%)" }}>
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10 text-center">
            <div className="mb-6 text-center">
              <div className="flex justify-center items-center gap-2">
                <a href="https://gotandem.ai" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 hover:opacity-90">
                  <img className="h-12 w-auto" src="/images/tandem-logo.svg" alt="Tandem" />
                  <span className="text-2xl font-bold text-gray-900">Tandem</span>
                </a>
              </div>
              <h2 className="mt-4 text-2xl font-extrabold text-gray-900">Profile complete!</h2>
              <p className="mt-1 text-sm text-gray-600">Welcome to Tandem. Redirecting to your dashboard...</p>
            </div>
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-tandem-blue mx-auto"></div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex flex-col justify-center py-12 sm:px-6 lg:px-8" style={{ background: "linear-gradient(135deg, #36d1dc 0%, #5b86e5 100%)" }}>
      <div className="sm:mx-auto sm:w-full sm:max-w-2xl">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          {/* Header */}
          <div className="text-center mb-8">
            <img 
              className="h-12 w-auto mx-auto mb-4" 
              src="/images/tandem-logo.svg" 
              alt="Tandem" 
            />
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              Welcome to Tandem! 🎉
            </h1>
            <p className="text-lg text-gray-600">
              Complete your profile and receive customized revenue opportunities!
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Role */}
            <div>
              <label htmlFor="role" className="block text-sm font-medium text-gray-700">
                Your Role
              </label>
              <div className="mt-1 relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <User className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="role"
                  name="role"
                  type="text"
                  required
                  className={`input pl-10 ${errors.role ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : ''}`}
                  placeholder="e.g., Product Manager, Account Executive, etc."
                  value={formData.role}
                  onChange={handleChange}
                />
              </div>
              {errors.role && <p className="mt-1 text-sm text-red-600">{errors.role}</p>}
            </div>

            {/* Team */}
            <div>
              <label htmlFor="team" className="block text-sm font-medium text-gray-700">
                Your Team
              </label>
              <select
                id="team"
                name="team"
                required
                className={`input ${errors.team ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : ''}`}
                value={formData.team}
                onChange={handleChange}
              >
                <option value="">Select your team...</option>
                <option value="sales">Sales</option>
                <option value="customer_success">Customer Success</option>
                <option value="product_management">Product Management</option>
                <option value="program_management">Program Management</option>
                <option value="product_marketing">Product Marketing</option>
                <option value="operations">Operations</option>
                <option value="other">Other</option>
              </select>
              {errors.team && <p className="mt-1 text-sm text-red-600">{errors.team}</p>}
            </div>

            {/* Product Value Proposition */}
            <div>
              <label htmlFor="product_value" className="block text-sm font-medium text-gray-700">
                Product Value Propositions (2-3 sentences)
              </label>
              <textarea
                id="product_value"
                name="product_value"
                rows={3}
                required
                className={`input ${errors.product_value ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : ''}`}
                placeholder="e.g., Tandem helps B2B teams discover and act on revenue opportunities faster."
                value={formData.product_value}
                onChange={handleChange}
              />
              {errors.product_value && <p className="mt-1 text-sm text-red-600">{errors.product_value}</p>}
            </div>

            {/* Industry */}
            <div>
              <label htmlFor="industry" className="block text-sm font-medium text-gray-700">
                Industry
              </label>
              <select
                id="industry"
                name="industry"
                required
                className={`input ${errors.industry ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : ''}`}
                value={formData.industry}
                onChange={handleChange}
              >
                <option value="">Select your industry...</option>
                <option value="Technology/Software">Technology/Software</option>
                <option value="SaaS/Cloud Services">SaaS/Cloud Services</option>
                <option value="Financial Technology">Financial Technology</option>
                <option value="Healthcare/Medical">Healthcare/Medical</option>
                <option value="E-commerce/Retail">E-commerce/Retail</option>
                <option value="Manufacturing">Manufacturing</option>
                <option value="Consulting/Services">Consulting/Services</option>
                <option value="Education/EdTech">Education/EdTech</option>
                <option value="Media/Entertainment">Media/Entertainment</option>
                <option value="Real Estate">Real Estate</option>
                <option value="Other">Other</option>
              </select>
              {errors.industry && <p className="mt-1 text-sm text-red-600">{errors.industry}</p>}
            </div>

            {/* Business Focus */}
            <div>
              <label htmlFor="business_focus" className="block text sm font-medium text-gray-700">
                Business Focus Areas
              </label>
              <div className="mt-1 relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Target className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="business_focus"
                  name="business_focus"
                  type="text"
                  required
                  className={`input pl-10 ${errors.business_focus ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : ''}`}
                  placeholder="e.g., Revenue Operations, Customer Retention, Product Launches"
                  value={formData.business_focus}
                  onChange={handleChange}
                />
              </div>
              {errors.business_focus && <p className="mt-1 text-sm text-red-600">{errors.business_focus}</p>}
            </div>

            {/* Customer Segments */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Target Customer Segments
              </label>
              <div className="grid grid-cols-2 gap-3">
                {['Startup', 'SMB', 'Enterprise', 'Nonprofit', 'Government', 'Other'].map((segment) => (
                  <label key={segment} className="flex items-center">
                    <input
                      type="checkbox"
                      name="customer_segments"
                      value={segment}
                      checked={formData.customer_segments.includes(segment)}
                      onChange={(e) => handleCheckboxChange('customer_segments', segment, e.target.checked)}
                      className="h-4 w-4 text-tandem-blue focus:ring-tandem-blue border-gray-300 rounded"
                    />
                    <span className="ml-2 text-sm text-gray-700">{segment}</span>
                  </label>
                ))}
              </div>
              {errors.customer_segments && <p className="mt-1 text-sm text-red-600">{errors.customer_segments}</p>}
            </div>

            {/* Customer Locations */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Target Customer Locations
              </label>
              <div className="grid grid-cols-2 gap-3">
                {['North America', 'Europe', 'Asia-Pacific', 'Middle East', 'Latin America', 'Africa', 'Global'].map((location) => (
                  <label key={location} className="flex items-center">
                    <input
                      type="checkbox"
                      name="customer_locations"
                      value={location}
                      checked={formData.customer_locations.includes(location)}
                      onChange={(e) => handleCheckboxChange('customer_locations', location, e.target.checked)}
                      className="h-4 w-4 text-tandem-blue focus:ring-tandem-blue border-gray-300 rounded"
                    />
                    <span className="ml-2 text-sm text-gray-700">{location}</span>
                  </label>
                ))}
              </div>
              {errors.customer_locations && <p className="mt-1 text-sm text-red-600">{errors.customer_locations}</p>}
            </div>

            {/* Submit Buttons */}
            <div className="flex flex-col sm:flex-row gap-4">
              <button
                type="submit"
                disabled={isLoading}
                className="flex-1 btn-primary flex justify-center items-center"
              >
                {isLoading ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                ) : (
                  <ArrowRight className="w-4 h-4 mr-2" />
                )}
                {isLoading ? 'Updating Profile...' : 'Update Profile'}
              </button>
              
              <button
                type="button"
                onClick={handleSkip}
                className="flex-1 btn-secondary"
              >
                Skip for Now
              </button>
            </div>
          </form>

          <div className="text-center mt-8">
            <p className="text-sm text-gray-500">
              © 2025 Tandem AI, Inc. All rights reserved
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}
